import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  ThemeProvider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  styled,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard,
  Report,
  People,
  Category,
  Business,
  Settings,
  Logout,
  AccountCircle,
  Assessment,
  Insights,
  AccessTime,
  SwapHoriz,
  Sync,
  FlightTakeoff,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { API_ORIGIN } from '../config/env';
import { aviationDarkTheme } from '../theme/aviationDarkTheme';
import { GlobalClock } from './GlobalClock';
import Radar from './Radar';
import { Breadcrumbs } from './Breadcrumbs';
import { toast } from '../utils/toast';

const DRAWER_WIDTH = 220;
const APPBAR_HEIGHT = 56;
const SIDEBAR_COLLAPSED_KEY = 'elog_sidebar_collapsed';
const APP_VERSION_KEY = 'elog_app_version';
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
}

const navigationItems: NavigationItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/app/dashboard' },
  { text: 'Events', icon: <Report />, path: '/app/incidents' },
  { text: 'Shift Handover', icon: <SwapHoriz />, path: '/app/shift-handover' },
  { text: 'Reports', icon: <Assessment />, path: '/app/reports' },
  { text: 'LHD Form', icon: <FlightTakeoff />, path: '/app/lhd-forms' },
  { text: 'Units', icon: <Business />, path: '/app/units', roles: ['admin'] },
  { text: 'Users', icon: <People />, path: '/app/users', roles: ['admin'] },
  { text: 'Categories', icon: <Category />, path: '/app/categories', roles: ['admin'] },
  { text: 'Monitor', icon: <Insights />, path: '/app/monitor', roles: ['admin'] },
  { text: 'Avi Sky Sync', icon: <Sync />, path: '/app/avisky-sync', roles: ['admin'] },
  { text: 'Configuration', icon: <Settings />, path: '/app/configuration', roles: ['admin'] },
  { text: 'Settings', icon: <Settings />, path: '/app/settings', roles: ['admin'] },
];

// Styled Main component for smooth transitions
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minHeight: '100vh',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginLeft: open ? 0 : `-${DRAWER_WIDTH}px`,
    width: open ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
  },
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Layout: React.FC = () => {
  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Desktop sidebar collapsed state - persisted in localStorage; reset when app version changes (new deploy)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const storedVersion = localStorage.getItem(APP_VERSION_KEY);
    if (storedVersion !== APP_VERSION) {
      localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
      localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
      return true; // Default open on new deploy
    }
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved !== 'true'; // Default to open unless explicitly collapsed
  });
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarKey, setAvatarKey] = useState(Date.now()); // For cache busting
  const { state, logout, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use state.user as the single source of truth
  const meUser = state.user as any;

  // Fetch user data (for avatar, department, etc.) - used on focus and profile update
  const fetchUserData = useCallback(async () => {
    try {
      // Use refreshAuth to ensure consistent data normalization and storage update
      if (refreshAuth) {
        await refreshAuth();
        setAvatarKey(Date.now()); // Force avatar refresh
      }
    } catch {
      // Silent fail
    }
  }, [refreshAuth]);

  // Refresh auth & user data when window gains focus (keeps permissions/buttons up to date)
  useEffect(() => {
    const handleFocus = () => {
      fetchUserData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchUserData]);

  // Listen for profile picture updates (custom event)
  useEffect(() => {
    const handleProfileUpdate = () => {
      fetchUserData();
    };
    window.addEventListener('profilePictureUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profilePictureUpdated', handleProfileUpdate);
  }, [fetchUserData]);

  // Mobile drawer toggle
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Desktop sidebar toggle with persistence
  const handleSidebarToggle = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!newState));
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    toast.success('Logged out successfully');
    navigate('/', { replace: true });
  };

  const handleNavigation = (path: string) => {
    const fullPath = path.startsWith('/app') ? path : `/app${path}`;
    navigate(fullPath);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Use only role name for admin check (not roleId) to avoid flash when Operator has role_id 1.
  // Show admin items ONLY when we have a known role and it is explicitly admin (never show all then hide for Operator).
  const role = (state.user as any)?.role;
  const roleName = String(typeof role === 'string' ? role : role?.name ?? '').toLowerCase().trim();
  const hasKnownRole = Boolean(state.user && role != null);
  const isAdmin = hasKnownRole && (roleName === 'admin' || roleName === 'super_admin' || roleName === 'superadmin');

  // Render navigation items immediately based on role
  const filteredNavigationItems = navigationItems.filter(item => {
    if (!item.roles) return true;
    const isAdminOnly = item.roles.includes('admin');
    return isAdminOnly ? isAdmin : true;
  });

  // Build avatar URL with cache busting – works on all pages (header is inside Layout).
  // Prefer profile_picture_url; fallback to building from profile_picture + username when API returns only filename.
  const getAvatarUrl = useCallback(() => {
    const url = meUser?.profile_picture_url;
    if (url && typeof url === 'string') {
      const baseUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${API_ORIGIN.replace(/\/$/, '')}${url}` : `${API_ORIGIN.replace(/\/$/, '')}/${url}`;
      return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${avatarKey}`;
    }
    const filename = meUser?.profile_picture;
    const username = meUser?.username;
    if (filename && username && typeof filename === 'string' && typeof username === 'string') {
      const path = `/uploads/profile-pictures/${encodeURIComponent(username)}/${encodeURIComponent(filename)}`;
      return `${API_ORIGIN.replace(/\/$/, '')}${path}?t=${avatarKey}`;
    }
    return undefined;
  }, [meUser?.profile_picture_url, meUser?.profile_picture, meUser?.username, avatarKey]);

  // Drawer content
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, justifyContent: 'space-between', px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.jpeg"
            alt="Aviation Electronic Logbook"
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0, 61, 122, 0.3)',
            }}
          />
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              color: '#00B8D4',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              maxWidth: 148,
              fontSize: '0.78rem',
            }}
          >
            Aviation Electronic Logbook
          </Typography>
        </Box>
        <Tooltip title="Hide Sidebar">
          <IconButton size="small" onClick={handleSidebarToggle} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, py: 1 }}>
        {filteredNavigationItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 1.5,
                py: 0.75,
                '&.Mui-selected': {
                  bgcolor: 'rgba(47,140,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(47,140,255,0.2)' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={aviationDarkTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />
        
        {/* AppBar - Fixed at top with compact height */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: 1200,
            height: APPBAR_HEIGHT,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...(sidebarOpen && {
              [theme.breakpoints.up('md')]: {
                width: `calc(100% - ${DRAWER_WIDTH}px)`,
                marginLeft: `${DRAWER_WIDTH}px`,
              },
            }),
          }}
        >
          <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, px: { xs: 1, sm: 2 } }}>
            {/* Mobile Menu Toggle */}
            <IconButton
              color="inherit"
              aria-label="open mobile drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Desktop Sidebar Toggle Button */}
            <Tooltip title={sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}>
              <IconButton
                color="inherit"
                aria-label="toggle sidebar"
                edge="start"
                onClick={handleSidebarToggle}
                sx={{
                  mr: 1.5,
                  display: { xs: 'none', md: 'flex' },
                  width: 36,
                  height: 36,
                  bgcolor: !sidebarOpen ? 'rgba(47,140,255,0.2)' : 'transparent',
                  border: !sidebarOpen ? '1px solid rgba(47,140,255,0.4)' : '1px solid transparent',
                  '&:hover': {
                    bgcolor: !sidebarOpen ? 'rgba(47,140,255,0.3)' : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {sidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
              </IconButton>
            </Tooltip>
            
            <Typography 
              variant="subtitle1" 
              noWrap 
              component="div" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 600,
                fontSize: { xs: '0.78rem', sm: '0.95rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Aviation Electronic Logbook
            </Typography>

            {/* Mini Radar - System Heartbeat (between title and clock) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
              <Radar size={52} syncWithClock incidents={[]} minimal />
            </Box>

            {/* Compact Header Info Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GlobalClock />
              
              {/* User Info Card - Compact with Labels */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  maxWidth: { xs: 200, sm: 280, md: 320 },
                }}
              >
                {/* User Details - Desktop only - Horizontal Compact Layout */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}>
                  {/* Row 1: Name and Role */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.8rem',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {meUser?.full_name || meUser?.username || 'User'}
                    </Typography>
                    {meUser?.job_title && (
                      <Typography 
                        component="span"
                        sx={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          fontWeight: 400,
                        }}
                      >
                        | {meUser.job_title}
                      </Typography>
                    )}
                    {meUser?.role?.name && (
                      <Typography 
                        component="span"
                        sx={{ 
                          fontSize: '0.65rem',
                          px: 0.75,
                          py: 0.15,
                          borderRadius: 0.5,
                          bgcolor: 'rgba(47,140,255,0.2)',
                          color: '#2F8CFF',
                          fontWeight: 500,
                        }}
                      >
                        {meUser.role.name}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Row 2: Unit | Dept | Last Login - Horizontal with separators */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    {(meUser?.unit?.name) && (
                      <>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.6, 
                            fontSize: '0.6rem',
                          }}
                        >
                          Unit:
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.85, 
                            fontSize: '0.6rem',
                            fontWeight: 500,
                          }}
                        >
                          {meUser?.unit?.name}
                        </Typography>
                      </>
                    )}
                    {(meUser?.department?.name) && (
                      <>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.4, 
                            fontSize: '0.6rem',
                            mx: 0.25,
                          }}
                        >
                          |
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.6, 
                            fontSize: '0.6rem',
                          }}
                        >
                          Dept:
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.85, 
                            fontSize: '0.6rem',
                            fontWeight: 500,
                          }}
                        >
                          {meUser?.department?.name}
                        </Typography>
                      </>
                    )}
                    {meUser?.last_login && (
                      <>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.4, 
                            fontSize: '0.6rem',
                            mx: 0.25,
                          }}
                        >
                          |
                        </Typography>
                        <AccessTime sx={{ fontSize: 9, opacity: 0.5 }} />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.6, 
                            fontSize: '0.55rem',
                          }}
                        >
                          {new Date(meUser.last_login).toLocaleString()}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
                
                {/* Avatar with menu */}
                <IconButton
                  size="small"
                  aria-label="account"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                  sx={{ p: 0.25 }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    color={meUser?.is_online ? 'success' : 'default'}
                    sx={{ '& .MuiBadge-dot': { width: 8, height: 8, border: '1px solid #1a1a2e' } }}
                  >
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                      src={getAvatarUrl()}
                      alt={(meUser?.full_name || 'User') as string}
                    >
                      {!getAvatarUrl() && (
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          {(meUser?.full_name || 'U').charAt(0).toUpperCase()}
                        </Typography>
                      )}
                    </Avatar>
                  </Badge>
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Profile Menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/app/profile'); }}>
            <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1.5, fontSize: 20 }} />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>

        {/* Mobile Drawer (temporary) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>
        
        {/* Desktop Drawer (persistent) */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.08)',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Main Content Area - Using styled component for proper transitions */}
        <Main open={sidebarOpen}>
          {/* Toolbar spacer */}
          <Box sx={{ height: APPBAR_HEIGHT }} />
          
          {/* Breadcrumbs */}
          <Breadcrumbs />
          
          {/* Page Content */}
          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Outlet />
          </Box>
        </Main>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
